import type { IMaterial } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';
import type { Part } from './partUtils';
import {
    getBasePartId,
    expandPartsWithQuantity
} from './partUtils';
import {
    getPatternPartsInfo,
    calculateMaxRepeats,
    createMaterialCopy
} from './materialUtils';

export interface UnplacedPartSummary {
    width: number;
    height: number;
    count: number;
}

export const calculateUnplacedParts = (
    parts: Part[],
    result: ICutResult | null
): UnplacedPartSummary[] => {
    if (!result) return [];

    const placedCounts: { [key: string]: number } = {};

    result.usedMaterials.forEach((mat) => {
        mat.children?.forEach((child) => {
            const originalPart = parts.find((p) =>
                child.id.startsWith(p.id + '-')
            );
            if (originalPart) {
                const key = `${originalPart.width}x${originalPart.height}`;
                placedCounts[key] = (placedCounts[key] || 0) + 1;
            }
        });
    });

    const unplacedSummary: UnplacedPartSummary[] = [];

    parts.forEach((part) => {
        const key = `${part.width}x${part.height}`;
        const placed = placedCounts[key] || 0;
        const unplaced = part.quantity - placed;

        if (unplaced > 0) {
            unplacedSummary.push({
                width: part.width,
                height: part.height,
                count: unplaced,
            });
        }
    });

    return unplacedSummary;
};

export const hasUnplacedParts = (parts: Part[], result: ICutResult | null): boolean => {
    if (!result) return false;

    const totalRequested = parts.reduce((sum, part) => sum + part.quantity, 0);
    const totalPlaced = result.usedMaterials.reduce(
        (sum, mat) => sum + (mat.children?.length || 0),
        0
    );

    return totalPlaced < totalRequested;
};

export const runMultiMaterialOptimization = async (
    material: IMaterial,
    parts: Part[],
    selectedStrategyId: string,
    optimizeFunction: (material: IMaterial, parts: IMaterial[]) => Promise<ICutResult | ICutResult[] | null>
): Promise<ICutResult | null> => {
    let remainingParts = expandPartsWithQuantity(parts);
    const allMaterialResults: ICutResult[] = [];
    let materialIndex = 1;

    while (remainingParts.length > 0) {
        const currentMaterial: IMaterial = {
            ...material,
            id: `material-${materialIndex}`,
            children: [],
        };

        const optimized = await optimizeFunction(currentMaterial, remainingParts);

        if (!optimized) {
            console.log(`No optimization result for material ${materialIndex}, breaking loop`);
            break;
        }

        // Handle both single result and array of tied results
        let bestOptimized: ICutResult;

        if (Array.isArray(optimized)) {
            // Multiple tied results - choose the one that would use fewer materials in the long run
            console.log(`Found ${optimized.length} tied results, comparing material usage...`);

            // Test each tied result to see which one leads to better overall material usage
            let bestChoice = optimized[0];
            let bestProjectedMaterials = Infinity;

            for (const candidate of optimized) {
                if (candidate.usedMaterials?.length) {
                    const placedPartIds: string[] = [];
                    candidate.usedMaterials.forEach((mat) => {
                        mat.children?.forEach((child) => {
                            placedPartIds.push(child.id);
                        });
                    });

                    // Calculate how many parts this candidate places
                    const partsPlaced = placedPartIds.length;
                    const remainingAfterThis = remainingParts.length - partsPlaced;

                    // Estimate how many more materials would be needed
                    // This is a simple heuristic - you could make it more sophisticated
                    const estimatedAdditionalMaterials = Math.ceil(remainingAfterThis / Math.max(1, partsPlaced));
                    const totalProjectedMaterials = 1 + estimatedAdditionalMaterials;

                    console.log(`Candidate places ${partsPlaced} parts, estimated total materials: ${totalProjectedMaterials}`);

                    if (totalProjectedMaterials < bestProjectedMaterials) {
                        bestProjectedMaterials = totalProjectedMaterials;
                        bestChoice = candidate;
                    }
                }
            }

            bestOptimized = bestChoice;
            bestOptimized.strategyName = `${bestOptimized.strategyName} - Selected from ${optimized.length} tied results (best material efficiency)`;

        } else {
            // Single result
            bestOptimized = optimized;
        }

        if (!bestOptimized?.usedMaterials?.length) {
            console.log(`No usable materials in optimization result for material ${materialIndex}, breaking loop`);
            break;
        }

        const placedPartIds: string[] = [];
        bestOptimized.usedMaterials.forEach((mat) => {
            mat.children?.forEach((child) => {
                placedPartIds.push(child.id);
            });
        });

        if (placedPartIds.length === 0) {
            console.log(`No parts placed on material ${materialIndex}, breaking loop`);
            break;
        }

        const optimalPattern = bestOptimized.usedMaterials[0];
        const patternPartsInfo = getPatternPartsInfo(optimalPattern, parts);
        const maxRepeats = calculateMaxRepeats(remainingParts, patternPartsInfo, parts);

        console.log(`Pattern can be repeated ${maxRepeats} times`);

        for (let copyIndex = 0; copyIndex < maxRepeats; copyIndex++) {
            const materialCopy = createMaterialCopy(
                optimalPattern,
                materialIndex + copyIndex,
                remainingParts,
                parts
            );

            if (materialCopy?.children?.length) {
                const materialResult: ICutResult = {
                    ...bestOptimized,
                    usedMaterials: [materialCopy],
                    strategyName: `${bestOptimized.strategyName} - Material ${materialIndex + copyIndex}`,
                };

                allMaterialResults.push(materialResult);

                const usedPartIds = materialCopy.children.map((child) => child.id);
                remainingParts = remainingParts.filter(
                    (part) => !usedPartIds.includes(part.id)
                );
            }
        }

        materialIndex += maxRepeats;

        if (materialIndex > 100 || remainingParts.length === 0) {
            break;
        }
    }

    if (allMaterialResults.length === 0) {
        return null;
    }

    // Combine all results
    const combinedResult: ICutResult = {
        usedMaterials: [],
        wastePercentage: 0,
        strategyName: `${selectedStrategyId} - Multi-Material`,
        debugSteps: [],
    };

    allMaterialResults.forEach((res) => {
        combinedResult.usedMaterials.push(...res.usedMaterials);
        if (res.debugSteps) {
            combinedResult.debugSteps?.push(...res.debugSteps);
        }
    });

    // Calculate overall waste percentage
    const totalMaterialArea = combinedResult.usedMaterials.reduce(
        (sum, mat) => sum + mat.width * mat.height,
        0
    );

    const totalUsedArea = combinedResult.usedMaterials.reduce((sum, mat) => {
        const usedArea = (mat.children || []).reduce(
            (childSum, child) => childSum + child.width * child.height,
            0
        );
        return sum + usedArea;
    }, 0);

    combinedResult.wastePercentage =
        totalMaterialArea > 0
            ? ((totalMaterialArea - totalUsedArea) / totalMaterialArea) * 100
            : 0;

    const totalPartsPlaced = combinedResult.usedMaterials.reduce(
        (sum, mat) => sum + (mat.children?.length || 0),
        0
    );

    combinedResult.strategyName = `${selectedStrategyId} - ${allMaterialResults.length
        } Sheet(s), ${totalPartsPlaced} Parts, ${combinedResult.wastePercentage.toFixed(
            2
        )}% Waste`;

    return combinedResult;
};